import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Brain, Send, ArrowRight, Sparkles, Check } from 'lucide-react';
interface Step {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  details: string[];
}
export function HowItWorksSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const steps: Step[] = [{
    number: 1,
    title: 'Connect Your Markets',
    description: 'Select your target cities and set your investment criteria. AcquireFlow syncs with MLS data in real-time.',
    icon: <MapPin className="w-6 h-6" strokeWidth={2} data-id="element-456" />,
    details: ['Select target cities', 'Set investment criteria', 'Real-time MLS sync']
  }, {
    number: 2,
    title: 'AI Analyzes Properties',
    description: 'Our AI scores every property, calculates optimal offers, and identifies motivated sellers automatically.',
    icon: <Brain className="w-6 h-6" strokeWidth={2} data-id="element-457" />,
    details: ['Smart property scoring', 'Automated offer calculation', 'Motivated seller detection']
  }, {
    number: 3,
    title: 'Send Offers Automatically',
    description: '1,000+ personalized offers sent per month. Track responses, follow-ups, and conversions in one dashboard.',
    icon: <Send className="w-6 h-6" strokeWidth={2} data-id="element-458" />,
    details: ['1,000+ offers per month', 'Fully personalized', 'Automated tracking']
  }];
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      });
    }, {
      threshold: 0.2
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section id="how-it-works" ref={sectionRef} className="relative w-full overflow-hidden py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-8" data-id="element-459">
      <div className="relative z-10 max-w-[1400px] mx-auto" data-id="element-460">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20" data-id="element-461">
          <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-4 sm:mb-6 md:mb-8 backdrop-blur-sm transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-462">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#3ab795]" data-id="element-463" />
            <span className="text-xs sm:text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-464">
              How It Works
            </span>
          </div>

          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-[1.1] tracking-tight transition-all duration-700 delay-100 px-4 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-465">
            <span className="block text-[#1D1D1D]" data-id="element-466">From MLS to Mailbox</span>
            <span className="block text-[#3ab795]" data-id="element-467">in 3 Simple Steps</span>
          </h2>

          <p className={`text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 px-4 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-468">
            Setup takes 15 minutes. Results come in days, not months.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-6 mb-10 sm:mb-12 md:mb-16 relative" data-id="element-469">
          {/* Connector Lines - Desktop Only */}
          <div className="hidden lg:block absolute top-[60px] left-0 right-0 pointer-events-none z-0" data-id="element-470">
            <div className="flex items-center justify-between px-[calc(16.666%+2rem)]" data-id="element-471">
              {/* Line 1 to 2 */}
              <div className={`h-[2px] w-[calc(33.333%-8rem)] relative transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`} style={{
              transformOrigin: 'left',
              transitionDelay: '800ms'
            }} data-id="element-472">
                <div className="absolute inset-0 bg-gradient-to-r from-[#3ab795]/40 to-[#3ab795]/10" data-id="element-473" />
                <div className="absolute right-0 top-1/2 -translate-y-1/2 -mr-1" data-id="element-474">
                  <div className="w-2 h-2 rounded-full bg-[#3ab795]/40" data-id="element-475" />
                </div>
              </div>

              {/* Line 2 to 3 */}
              <div className={`h-[2px] w-[calc(33.333%-8rem)] relative transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`} style={{
              transformOrigin: 'left',
              transitionDelay: '1200ms'
            }} data-id="element-476">
                <div className="absolute inset-0 bg-gradient-to-r from-[#3ab795]/40 to-[#3ab795]/10" data-id="element-477" />
                <div className="absolute right-0 top-1/2 -translate-y-1/2 -mr-1" data-id="element-478">
                  <div className="w-2 h-2 rounded-full bg-[#3ab795]/40" data-id="element-479" />
                </div>
              </div>
            </div>
          </div>

          {steps.map((step, index) => <div key={step.number} className={`relative transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
          transitionDelay: `${400 + index * 200}ms`
        }} data-id="element-480">
              {/* Floating number badge */}
              <div className="absolute -top-3 sm:-top-4 -left-3 sm:-left-4 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#3ab795] to-[#2d9f7e] text-white font-bold text-lg sm:text-xl flex items-center justify-center shadow-lg z-10" data-id="element-481">
                {step.number}
              </div>

              {/* Card with colored left border */}
              <div className="relative pt-5 sm:pt-6 p-5 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl bg-white border-l-4 border-[#3ab795] shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-482">
                {/* Subtle dot pattern background */}
                <div className="absolute inset-0 opacity-[0.02] rounded-xl sm:rounded-2xl" style={{
              backgroundImage: `radial-gradient(circle, #3ab795 1px, transparent 1px)`,
              backgroundSize: '20px 20px'
            }} data-id="element-483" />

                {/* Content */}
                <div className="relative z-10" data-id="element-484">
                  {/* Icon and Title Row */}
                  <div className="mb-3 sm:mb-4 flex items-start gap-2.5 sm:gap-3" data-id="element-485">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#3ab795]/10 flex items-center justify-center text-[#3ab795]" data-id="element-486">
                      {step.icon}
                    </div>
                    <div className="flex-1 pt-0.5 sm:pt-1" data-id="element-487">
                      <h3 className="text-lg sm:text-xl font-bold text-[#1D1D1D] leading-tight" data-id="element-488">
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-[15px] mb-4 sm:mb-5" data-id="element-489">
                    {step.description}
                  </p>

                  {/* Details with always-visible checkmarks */}
                  <div className="space-y-2 sm:space-y-2.5" data-id="element-490">
                    {step.details.map((detail, idx) => <div key={idx} className="flex items-center gap-2 sm:gap-2.5 text-xs sm:text-sm text-gray-600" data-id="element-491">
                        <div className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 rounded-md bg-[#3ab795] flex items-center justify-center shadow-sm" data-id="element-492">
                          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" strokeWidth={3} data-id="element-493" />
                        </div>
                        <span className="font-medium" data-id="element-494">{detail}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Mobile Connector */}
              {index < steps.length - 1 && <div className="lg:hidden flex justify-center my-6 sm:my-8" data-id="element-495">
                  <div className={`w-[2px] h-10 sm:h-12 bg-gradient-to-b from-[#3ab795]/40 to-transparent relative transition-all duration-700 ${isVisible ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'}`} style={{
              transformOrigin: 'top',
              transitionDelay: `${600 + index * 200}ms`
            }} data-id="element-496">
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 -mb-1" data-id="element-497">
                      <div className="w-2 h-2 rounded-full bg-[#3ab795]/40" data-id="element-498" />
                    </div>
                  </div>
                </div>}
            </div>)}
        </div>

        {/* CTA */}
        <div className={`text-center transition-all duration-700 delay-1000 px-4 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-499">
          <a href="/trial" className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-[#3ab795] text-white font-semibold text-sm sm:text-base rounded-xl transition-all duration-300 hover:bg-[#2d9f7e] hover:shadow-lg hover:shadow-[#3ab795]/25 group" data-id="element-500">
            <span data-id="element-501">Join Beta</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} data-id="element-502" />
          </a>
          <p className="text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4" data-id="element-503">
            45-day trial • Lifetime 50% discount
          </p>
        </div>
      </div>
    </section>;
}